// AdminPanel.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  AppBar,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleIcon from '@mui/icons-material/People';
import StoreIcon from '@mui/icons-material/Store';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RegisterAdmin from './AdminRegister'; // Import the new RegisterAdmin component
import SignupGrowthChart from './SignupGrowthChart';
import ResellerSignupChart from './ResellerSignupChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ViewRequests from './ViewRequests'; // adjust path if needed
import MailOutlineIcon from '@mui/icons-material/MailOutline'; // Optional new icon
import CategoryIcon from '@mui/icons-material/Category';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import CategoryData from './categoryData'; // Import the new CategoryData component
import SubCategoryPage from './SubCategoryPage'; // Import the new SubCategoryPage component





// Dummy components for pages
import UserData from './UserData';
import AdminPage from './AdminPage';

const drawerWidth = 240;

const AdminPanel = () => {
  const [open, setOpen] = useState(true);
  const [selectedPage, setSelectedPage] = useState('registerUser');
  const [admin, setAdmin] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('admin');
    if (stored) {
      setAdmin(JSON.parse(stored));
    } else {
      navigate('/admin-login?auth=required');
    }
  }, [navigate]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleMenuClick = (page) => {
    if (page === 'logout') {
      const confirmLogout = window.confirm('Are you sure you want to logout?');
      if (confirmLogout) {
        localStorage.clear();
        navigate('/admin-login');
      }
    } else {
      setSelectedPage(page);
      if (isMobile) setOpen(false);
    }
  };
const menuItems = [
  { text: 'Register User', icon: <PeopleIcon />, page: 'registerUser' },
  { text: 'Register Seller', icon: <StoreIcon />, page: 'registerSeller' },
  { text: 'Add Admins', icon: <PersonAddIcon />, page: 'addAdmin' },
  { text: 'View Requests', icon: <MailOutlineIcon />, page: 'viewRequests' },

  // ✅ New Menu Items
  { text: 'Manage Categories', icon: <CategoryIcon />, page: 'category' },
  { text: 'Manage Subcategories', icon: <SubdirectoryArrowRightIcon />, page: 'subcategory' },

  { text: 'User Signups Chart', icon: <BarChartIcon />, page: 'userChart' },
  { text: 'Reseller Signups Chart', icon: <ShowChartIcon />, page: 'resellerChart' },
  { text: 'Logout', icon: <LogoutIcon />, page: 'logout' },
];


const renderPage = () => {
  switch (selectedPage) {
    case 'registerUser':
      return <UserData />;
    case 'registerSeller':
      return <AdminPage />;
    case 'addAdmin':
      return <RegisterAdmin />;
    case 'viewRequests':
      return <ViewRequests />;
    case 'category':
      return <CategoryData />;  // ✅ new
    case 'subcategory':
      return <SubCategoryPage />;  // ✅ new
    case 'userChart':
      return <SignupGrowthChart />;
    case 'resellerChart':
      return <ResellerSignupChart />;
    default:
      return <Typography>Page not found</Typography>;
  }
};



  const drawerContent = (
    <Box sx={{ width: drawerWidth }}>
      <Toolbar />
      <List>
        {menuItems.map(({ text, icon, page }) => (
          <ListItem key={text} disablePadding>
            <ListItemButton onClick={() => handleMenuClick(page)}>
              <ListItemIcon>{icon}</ListItemIcon>
              <ListItemText primary={text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: '#2C3E50' }}
      >
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={handleToggle} sx={{ mr: 2 }}>
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            Admin Dashboard
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {admin && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" sx={{ color: 'white', fontWeight: 'bold' }}>
                {admin.username || 'Admin'}
              </Typography>
              <Avatar
                alt="Admin"
                src={admin.profileImage || '/assets/admin-avatar.png'}
                sx={{ width: 40, height: 40, border: '2px solid #fff' }}
              />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={open}
        onClose={handleToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        {renderPage()}
      </Box>
    </Box>
  );
};

export default AdminPanel;
